import { Link, useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft, Bell, Menu, Home, Briefcase, Plus, Compass, MoreHorizontal } from "lucide-react";
import MspInstallBanner from "./MspInstallBanner";
import MspAvatar from "./MspAvatar";

/* Phone-style shell for /msp/* pages so the web pages look like the app
   mockups (title bar, editorial serif heading, bottom nav). */

const BOTTOM = [
  { to: "/msp",             label: "Home",   Icon: Home },
  { to: "/msp/trips",       label: "Trips",  Icon: Briefcase },
  { to: "/msp/plan",        label: "Plan",   Icon: Plus,       fab: true },
  { to: "/msp/maps",        label: "Explore",Icon: Compass },
  { to: "/msp/documents",   label: "More",   Icon: MoreHorizontal },
];

export default function MspShell({ title, back = true, children, testid = "msp-page" }) {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const isChat = pathname === "/msp/ai-assistant";
  return (
    <div className={`min-h-[100svh] bg-[#F4F8FE] ${isChat ? "pb-40" : "pb-24"} msp-shell`}
      data-testid="msp-page"
      data-page={testid}>
      <style>{`
        @keyframes mspFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        .msp-shell main { animation: mspFadeIn 260ms cubic-bezier(0.22, 0.61, 0.36, 1); }
        .msp-shell nav[data-testid="msp-bottom-nav"] a:active { transform: scale(0.94); transition: transform 90ms; }
      `}</style>
      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 py-3">
          {back ? (
            <button onClick={() => nav(-1)} aria-label="Back" data-testid="msp-back"
              className="w-9 h-9 rounded-full hover:bg-slate-100 grid place-items-center text-slate-700">
              <ChevronLeft className="w-5 h-5" />
            </button>
          ) : (
            <button aria-label="Menu" data-testid="msp-menu"
              className="w-9 h-9 rounded-full hover:bg-slate-100 grid place-items-center text-slate-700">
              <Menu className="w-5 h-5" />
            </button>
          )}
          <Link to="/msp" className="flex items-center gap-2 mx-auto" data-testid="msp-brand">
            <span className="text-lg font-serif font-800 tracking-tight text-blue-700">My Social Pulse</span>
            <sup className="text-[10px] text-blue-600/70">™</sup>
          </Link>
          <button aria-label="Alerts" data-testid="msp-bell"
            className="w-9 h-9 rounded-full hover:bg-slate-100 grid place-items-center text-slate-700 relative">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1.5 right-2 w-2 h-2 rounded-full bg-rose-500" />
          </button>
          <MspAvatar />
        </div>
        <div className="max-w-3xl mx-auto px-5 pb-3">
          <h1 className="font-serif font-800 text-3xl text-blue-900 tracking-tight" data-testid="msp-title">{title}</h1>
        </div>
      </div>

      {/* Body — pathname-keyed for a soft fade-in on every /msp route change */}
      <main key={pathname} className="max-w-3xl mx-auto px-4 sm:px-5 pt-5 space-y-5">
        {children}
      </main>

      {/* Bottom tab bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200 pb-[env(safe-area-inset-bottom)]" data-testid="msp-bottom-nav">
        <div className="max-w-3xl mx-auto grid grid-cols-5 items-end">
          {BOTTOM.map(({ to, label, Icon, fab }) => {
            const active = pathname === to || (to !== "/msp" && pathname.startsWith(to));
            if (fab) {
              return (
                <Link key={label} to={to} data-testid={`msp-tab-${label.toLowerCase()}`}
                  className="flex flex-col items-center justify-end pt-1 pb-2">
                  <span className="w-14 h-14 rounded-full bg-blue-600 text-white grid place-items-center shadow-lg -mt-6 ring-4 ring-white">
                    <Icon className="w-6 h-6" />
                  </span>
                  <span className="text-[10px] font-600 text-slate-600 mt-1">{label}</span>
                </Link>
              );
            }
            return (
              <Link key={label} to={to} data-testid={`msp-tab-${label.toLowerCase()}`}
                className={`flex flex-col items-center gap-1 py-2 ${active ? "text-blue-600" : "text-slate-500"}`}>
                <Icon className={`w-5 h-5 ${active ? "" : "opacity-80"}`} strokeWidth={active ? 2.25 : 1.75} />
                <span className="text-[11px] font-600">{label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
